<script setup>
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { ComputerDesktopIcon, MoonIcon, SunIcon } from "@heroicons/vue/24/solid";
import { Bars3Icon, XMarkIcon } from "@heroicons/vue/24/outline";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useColorMode } from "@vueuse/core";
import WordmarkLogo from "./WordmarkLogo.vue";
import { useAuthStore } from "../pinia/auth.ts";

const { t } = useI18n();

defineProps({
  theme: {
    type: String,
    default: "grape",
  },
});

const authStore = useAuthStore();

const route = useRoute();
const router = useRouter();

const mode = useColorMode({
  initialValue: "dark",
  emitAuto: true,
});

function handleChangeMode() {
  const modes = ["light", "dark", "auto"];

  const currentIndex = modes.indexOf(mode.value);
  const nextIndex = (currentIndex + 1) % modes.length;
  mode.value = modes[nextIndex];
}

const navigation = [{ name: t("msg.home"), route: "/home" }];

const logout = () => {
  try {
    authStore.logout();
  } finally {
    router.push({ name: "Login" });
  }
};
</script>

<template>
  <Disclosure as="nav" class="border-b border-gray-300 bg-background text-lg font-semibold shadow" v-slot="{ open }">
    <div class="mx-auto max-w-screen-xl px-2 sm:px-6 lg:px-8">
      <div class="flex justify-between">
        <div class="flex py-4">
          <div class="flex items-center sm:hidden">
            <DisclosureButton
              class="relative inline-flex items-center justify-center rounded-md p-2 focus:outline-none"
            >
              <span class="absolute -inset-0.5" />
              <span class="sr-only">{{ $t("msg.openMainMenu") }}</span>
              <Bars3Icon v-if="!open" class="block h-6 w-6" aria-hidden="true" />
              <XMarkIcon v-else class="block h-6 w-6" aria-hidden="true" />
            </DisclosureButton>
          </div>

          <div class="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
            <div class="flex flex-shrink-0 items-center">
              <WordmarkLogo class="h-24 w-auto" />
            </div>
          </div>
        </div>

        <div class="flex">
          <div class="hidden sm:flex">
            <button
              class="flex shrink-0 basis-[128px] items-center justify-center px-8 text-center"
              :class="{
                'hover:bg-theme-teal': theme == 'teal',
                'hover:bg-theme-teal-hc': theme == 'teal-hc',
                'hover:bg-theme-grape': theme == 'grape',
                'hover:bg-theme-grape-hc': theme == 'grape-hc',
                'hover:bg-theme-iceberg': theme == 'iceberg',
                'hover:bg-theme-iceberg-hc': theme == 'iceberg-hc',
                'hover:bg-theme-rose': theme == 'rose',
                'hover:bg-theme-rose-hc': theme == 'rose-hc',
                'hover:bg-theme-offsite': theme == 'offsite',
                'hover:bg-theme-offsite-hc': theme == 'offsite-hc',
              }"
              @click="handleChangeMode"
            >
              <template v-if="mode === 'light'">
                <SunIcon aria-label="Light mode" class="h-5 w-5" />
              </template>
              <template v-else-if="mode === 'dark'">
                <MoonIcon aria-label="Dark mode" class="h-5 w-5" />
              </template>
              <template v-else-if="mode === 'auto'">
                <ComputerDesktopIcon aria-label="System mode" class="h-5 w-5" />
              </template>
            </button>
            <template v-for="item in navigation" :key="item.name">
              <router-link
                :to="item.route"
                class="flex shrink-0 basis-[128px] items-center justify-center border-x border-gray-300 border-x-white px-8 text-center"
                :class="{
                  'bg-theme-teal': theme == 'teal' && route.fullPath == item.route,
                  'bg-theme-teal-hc': theme == 'teal-hc' && route.fullPath == item.route,
                  'hover:bg-theme-teal': theme == 'teal',
                  'hover:bg-theme-teal-hc': theme == 'teal-hc',
                  'bg-theme-grape': theme == 'grape' && route.fullPath == item.route,
                  'bg-theme-grape-hc': theme == 'grape-hc' && route.fullPath == item.route,
                  'hover:bg-theme-grape': theme == 'grape',
                  'hover:bg-theme-grape-hc': theme == 'grape-hc',
                  'bg-theme-iceberg': theme == 'iceberg' && route.fullPath == item.route,
                  'bg-theme-iceberg-hc': theme == 'iceberg-hc' && route.fullPath == item.route,
                  'hover:bg-theme-iceberg': theme == 'iceberg',
                  'hover:bg-theme-iceberg-hc': theme == 'iceberg-hc',
                  'bg-theme-rose': theme == 'rose' && route.fullPath == item.route,
                  'bg-theme-rose-hc': theme == 'rose-hc' && route.fullPath == item.route,
                  'hover:bg-theme-rose': theme == 'rose',
                  'hover:bg-theme-rose-hc': theme == 'rose-hc',
                  'bg-theme-offsite': theme == 'offsite' && route.fullPath == item.route,
                  'bg-theme-offsite-hc': theme == 'offsite-hc' && route.fullPath == item.route,
                  'hover:bg-theme-offsite': theme == 'offsite',
                  'hover:bg-theme-offsite-hc': theme == 'offsite-hc',

                  'cursor-default': route.fullPath == item.route,
                }"
              >
                {{ item.name }}
              </router-link>
            </template>
            <button
              class="flex shrink-0 basis-[128px] items-center justify-center px-8 text-center"
              :class="{
                'hover:bg-theme-teal': theme == 'teal',
                'hover:bg-theme-teal-hc': theme == 'teal-hc',
                'hover:bg-theme-grape': theme == 'grape',
                'hover:bg-theme-grape-hc': theme == 'grape-hc',
                'hover:bg-theme-iceberg': theme == 'iceberg',
                'hover:bg-theme-iceberg-hc': theme == 'iceberg-hc',
                'hover:bg-theme-rose': theme == 'rose',
                'hover:bg-theme-rose-hc': theme == 'rose-hc',
                'hover:bg-theme-offsite': theme == 'offsite',
                'hover:bg-theme-offsite-hc': theme == 'offsite-hc',
              }"
              @click="logout"
            >
              {{ t("msg.logout") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <DisclosurePanel class="sm:hidden">
      <div class="space-y-1 pb-4 pt-2">
        <DisclosureButton
          v-for="item in navigation"
          as="router-link"
          :to="item.route"
          class="block cursor-pointer border-l-4 border-transparent bg-background py-2 pl-3 pr-4 text-base font-medium"
          :class="{
            'border-theme-teal': theme == 'teal' && route.fullPath == item.route,
            'border-theme-teal-hc': theme == 'teal-hc' && route.fullPath == item.route,
            'hover:border-theme-teal': theme == 'teal',
            'hover:border-theme-teal-hc': theme == 'teal-hc',
            'border-theme-grape': theme == 'grape' && route.fullPath == item.route,
            'border-theme-grape-hc': theme == 'grape-hc' && route.fullPath == item.route,
            'hover:border-theme-grape': theme == 'grape',
            'hover:border-theme-grape-hc': theme == 'grape-hc',
            'border-theme-iceberg': theme == 'iceberg' && route.fullPath == item.route,
            'border-theme-iceberg-hc': theme == 'iceberg-hc' && route.fullPath == item.route,
            'hover:border-theme-iceberg': theme == 'iceberg',
            'hover:border-theme-iceberg-hc': theme == 'iceberg-hc',
            'border-theme-rose': theme == 'rose' && route.fullPath == item.route,
            'border-theme-rose-hc': theme == 'rose-hc' && route.fullPath == item.route,
            'hover:border-theme-rose': theme == 'rose',
            'hover:border-theme-rose-hc': theme == 'rose-hc',
            'border-theme-offsite': theme == 'offsite' && route.fullPath == item.route,
            'border-theme-offsite-hc': theme == 'offsite-hc' && route.fullPath == item.route,
            'hover:border-theme-offsite': theme == 'offsite',
            'hover:border-theme-offsite-hc': theme == 'offsite-hc',
          }"
        >
          {{ item.name }}
        </DisclosureButton>
        <DisclosureButton
          as="div"
          @click="logout"
          class="block cursor-pointer border-l-4 border-transparent bg-background py-2 pl-3 pr-4 text-base font-medium"
          :class="{
            'hover:border-theme-teal': theme == 'teal',
            'hover:border-theme-teal-hc': theme == 'teal-hc',
            'hover:border-theme-grape': theme == 'grape',
            'hover:border-theme-grape-hc': theme == 'grape-hc',
            'hover:border-theme-iceberg': theme == 'iceberg',
            'hover:border-theme-iceberg-hc': theme == 'iceberg-hc',
            'hover:border-theme-rose': theme == 'rose',
            'hover:border-theme-rose-hc': theme == 'rose-hc',
            'hover:border-theme-offsite': theme == 'offsite',
            'hover:border-theme-offsite-hc': theme == 'offsite-hc',
          }"
        >
          {{ t("msg.logout") }}
        </DisclosureButton>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>
